import { useLocation, useNavigate } from 'react-router-dom';
import { Clock, History, Inbox, TriangleAlert } from 'lucide-react';
import { cn, numero } from '@/lib/utils';
import { useContexto } from '@/hooks/useContexto';

export type DestinoAba = 'pendentes' | 'hoje' | 'historico' | 'negativos';

/**
 * Negativos é uma ABA de Movimentos, não um quinto item de menu — mas tem
 * rota própria (`/movimentos/negativos`) para poder ser linkada do alerta do
 * Início e de uma posição vermelha no mapa.
 */
export function AbasMovimentos({
  ativa,
  totalNegativos,
}: {
  ativa: DestinoAba;
  totalNegativos?: number | undefined;
}): JSX.Element {
  const navegar = useNavigate();
  const local = useLocation();
  const { centro } = useContexto();
  const sufixo = centro === null ? '' : `centro=${centro}`;

  const ir = (destino: DestinoAba): void => {
    if (destino === 'negativos') {
      navegar(`/movimentos/negativos${sufixo ? `?${sufixo}` : ''}`);
      return;
    }
    const params = new URLSearchParams(local.search);
    params.set('aba', destino);
    navegar(`/movimentos?${params.toString()}`);
  };

  const abas: {
    chave: DestinoAba;
    rotulo: string;
    icone: JSX.Element;
    contagem?: number | undefined;
  }[] = [
    { chave: 'pendentes', rotulo: 'Pendentes', icone: <Inbox className="h-4 w-4" /> },
    { chave: 'hoje', rotulo: 'Hoje', icone: <Clock className="h-4 w-4" /> },
    { chave: 'historico', rotulo: 'Histórico', icone: <History className="h-4 w-4" /> },
    {
      chave: 'negativos',
      rotulo: 'Negativos',
      icone: <TriangleAlert className="h-4 w-4" />,
      contagem: totalNegativos,
    },
  ];

  return (
    <div
      role="tablist"
      aria-label="Movimentos"
      className="flex flex-wrap items-center gap-1 rounded-xl border border-borda bg-superficie2 p-1 shadow-xs"
    >
      {abas.map((aba) => {
        const ativo = ativa === aba.chave;
        return (
          <button
            key={aba.chave}
            role="tab"
            type="button"
            aria-selected={ativo}
            onClick={() => ir(aba.chave)}
            className={cn(
              'flex min-h-[38px] items-center gap-2 rounded-lg px-3.5 text-[13px] font-semibold transition-all',
              ativo
                ? 'bg-superficie text-primaria shadow-cartao ring-1 ring-borda'
                : 'text-textoFraco hover:bg-superficie hover:text-texto',
            )}
          >
            {aba.icone}
            {aba.rotulo}
            {aba.contagem !== undefined && aba.contagem > 0 ? (
              <span className="tabular rounded-full bg-status-divergenciaFundo px-1.5 py-0.5 text-[10.5px] font-bold text-status-divergencia">
                {numero(aba.contagem)}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
